import { createSlice } from '@reduxjs/toolkit';
import { ClosingDateState } from './closing-date.state';
import { EngagementClosingDate } from './models/engagement-closing-date.model';
import { FetchEngagementClosingDate } from './thunks/fetch-engagement-closing-date.thunk';
import { ProposeClosingDate } from './thunks/propose-closing-date.thunk';
import { AcceptClosingDateProposal } from './thunks/accept-closing-date-proposal.thunk';
import { DeclineClosingDateProposal } from './thunks/decline-closing-date-proposal.thunk';
import { CreateClosingDateDiscussion } from './thunks/create-closing-date-discussion.thunk';
import { ConfirmClose } from './thunks/confirm-close.thunk';

const initialState: ClosingDateState = {
    closingDate: undefined,
    isLoading: false,
    isSubmitting: false,
    status: 'idle',
    error: '',
};

const closingDateSlice = createSlice({
    name: 'closingDate',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(FetchEngagementClosingDate.pending, (state) => {
                setPendingState(state);
            })
            .addCase(FetchEngagementClosingDate.rejected, (state, action) => {
                setRejectedState(
                    state,
                    action.error.message || 'Failed to fetch engagement closing date'
                );
            })
            .addCase(FetchEngagementClosingDate.fulfilled, (state, action) => {
                state.closingDate = action.payload;
                setSucceededState(state);
            })
            .addCase(ProposeClosingDate.pending, (state) => {
                setSubmittingPendingState(state);
            })
            .addCase(ProposeClosingDate.rejected, (state, action) => {
                setSubmittingRejectedState(
                    state,
                    action.error.message || 'Failed to propose closing date'
                );
            })
            .addCase(ProposeClosingDate.fulfilled, (state, action) => {
                setSubmittingFulfilledState(state, action.payload);
            })
            .addCase(AcceptClosingDateProposal.pending, (state) => {
                setSubmittingPendingState(state);
            })
            .addCase(AcceptClosingDateProposal.rejected, (state, action) => {
                setSubmittingRejectedState(
                    state,
                    action.error.message || 'Failed to accept closing date proposal'
                );
            })
            .addCase(AcceptClosingDateProposal.fulfilled, (state, action) => {
                setSubmittingFulfilledState(state, action.payload);
            })
            .addCase(DeclineClosingDateProposal.pending, (state) => {
                setSubmittingPendingState(state);
            })
            .addCase(DeclineClosingDateProposal.rejected, (state, action) => {
                setSubmittingRejectedState(
                    state,
                    action.error.message || 'Failed to decline closing date proposal'
                );
            })
            .addCase(DeclineClosingDateProposal.fulfilled, (state, action) => {
                setSubmittingFulfilledState(state, action.payload);
            })
            .addCase(CreateClosingDateDiscussion.pending, (state) => {
                setSubmittingPendingState(state);
            })
            .addCase(CreateClosingDateDiscussion.rejected, (state, action) => {
                setSubmittingRejectedState(
                    state,
                    action.error.message || 'Failed to create closing date discussion'
                );
            })
            .addCase(CreateClosingDateDiscussion.fulfilled, (state, action) => {
                setSubmittingFulfilledState(state, action.payload);
            })
            .addCase(ConfirmClose.pending, (state) => {
                setSubmittingPendingState(state);
            })
            .addCase(ConfirmClose.rejected, (state, action) => {
                setSubmittingRejectedState(
                    state,
                    action.error.message || 'Failed to confirm close'
                );
            })
            .addCase(ConfirmClose.fulfilled, (state, action) => {
                setSubmittingFulfilledState(state, action.payload);
            });
    },
});

const setRejectedState = (state: ClosingDateState, errorMessage: string) => {
    state.isLoading = false;
    state.status = 'failed';
    state.error = errorMessage;
};

const setPendingState = (state: ClosingDateState) => {
    state.isLoading = true;
    state.status = 'loading';
    state.error = '';
};

const setSucceededState = (state: ClosingDateState) => {
    state.isLoading = false;
    state.status = 'succeeded';
    state.error = '';
};

const setSubmittingPendingState = (state: ClosingDateState) => {
    state.isSubmitting = true;
    state.error = '';
};

const setSubmittingRejectedState = (
    state: ClosingDateState,
    errorMessage: string
) => {
    state.isSubmitting = false;
    state.error = errorMessage;
};

const setSubmittingFulfilledState = (
    state: ClosingDateState,
    closingDate: EngagementClosingDate
) => {
    state.closingDate = closingDate;
    state.isSubmitting = false;
    state.error = '';
};

export const {} = closingDateSlice.actions;
export default closingDateSlice.reducer;
